/**
 * Page Pourquoi Adhérer - FTPE Outremer
 */
import React from 'react';
import { CheckCircle, Users, Shield, Briefcase, Award, ArrowRight, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

// Logo FTPE Outremer
const FTPE_LOGO = "https://customer-assets.emergentagent.com/job_80d9c916-7281-4428-9c84-a6e0cd256c92/artifacts/ojac2aim_logo%20ftpe%20outremer%20dans%20fond%20blanc.png";

export default function FTPEAdhererPage() {
  const navigate = useNavigate();

  const avantages = [
    {
      icon: Users,
      title: "Représentation patronale",
      description: "Votre voix compte. Participez aux décisions et aux orientations du syndicat."
    },
    {
      icon: Shield,
      title: "Accès aux dispositifs partenaires",
      description: "Cadre institutionnel préalable pour accéder aux dispositifs solidaires (COPPAM) et services (O'SCOP).",
      note: "Sous conditions propres à chaque dispositif."
    },
    {
      icon: Briefcase,
      title: "Information & accompagnement",
      description: "Veille réglementaire, actualités sectorielles et ressources documentaires."
    },
    {
      icon: Award,
      title: "Réseau & événements",
      description: "Rencontres, échanges entre pairs et opportunités de développement."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 text-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <BackButton variant="dark" label="Retour" />
          </div>
          
          <div className="flex items-center gap-6 mb-8">
            <img src={FTPE_LOGO} alt="FTPE Outremer" className="w-20 h-20 object-contain rounded-lg bg-white p-2" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Pourquoi <span className="text-amber-400">adhérer</span> ?
              </h1>
              <p className="text-xl text-slate-300">Rejoignez le syndicat des TPE ultramarines</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-5xl mx-auto bg-white shadow-lg -mt-10 relative z-10 rounded-2xl p-8 md:p-12 mb-12">
        
        {/* Section Qui peut adhérer */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-4">Qui peut adhérer ?</h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            Toute entreprise ou employeur ultramarin répondant aux conditions statutaires
            peut demander son adhésion à la FTPE Outremer.
          </p>
        </section>

        {/* Avantages */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-6">Les avantages de l'adhésion</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {avantages.map((avantage, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border-l-4 border-amber-500">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <avantage.icon className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">{avantage.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{avantage.description}</p>
                    {avantage.note && (
                      <p className="text-xs text-amber-600 mt-2 italic">{avantage.note}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Cotisation */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-4">Cotisation annuelle</h2>
          <div className="bg-gradient-to-r from-amber-50 to-green-50 p-8 rounded-xl border border-amber-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <CreditCard className="w-10 h-10 text-amber-600" />
                <div>
                  <p className="text-3xl font-bold text-slate-900">200 € <span className="text-lg font-normal text-slate-600">/ an</span></p>
                  <p className="text-sm text-slate-600">Paiement sécurisé par carte bancaire</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/adhesion-ftpe-workflow')}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl"
              >
                Adhérer maintenant
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Note importante */}
        <section className="mb-8">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
            <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Important
            </h3>
            <p className="text-blue-700 text-sm leading-relaxed">
              L'adhésion à la FTPE Outremer constitue le cadre institutionnel requis pour accéder
              aux dispositifs solidaires portés par COPPAM ainsi qu'aux services proposés au sein
              de l'écosystème O'SCOP. Chaque garantie ou service reste soumis à ses conditions propres.
            </p>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 text-center">
        <p className="font-semibold">FTPE Outremer – Syndicat patronal ultramarin</p>
        <p className="text-slate-400 text-sm mt-2">Représentation • Accompagnement • Solidarité</p>
      </footer>
    </div>
  );
}
